<script setup>
import { computed } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
);

const props = defineProps({
  labels: {
    type: Array,
    default: () => [],
  },
  values: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: "Net cashflow",
  },
  format: {
    type: String,
    default: "currency",
  },
  tooltipRows: {
    type: Function,
    default: null,
  },
});

const isCurrency = computed(() => props.format === "currency");
const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
});
const daysFormatter = new Intl.NumberFormat("en-GB", {
  maximumFractionDigits: 1,
});

const formatValue = (value) => {
  if (isCurrency.value) {
    return currencyFormatter.format(value);
  }
  return `${daysFormatter.format(value)} days`;
};

const chartData = computed(() => ({
  labels: props.labels,
  datasets: [
    {
      label: props.label,
      data: props.values.map((value) =>
        isCurrency.value ? Number(value ?? 0) / 100 : Number(value ?? 0),
      ),
      borderColor: "rgba(14, 116, 144, 0.9)",
      backgroundColor: "rgba(14, 116, 144, 0.12)",
      pointBackgroundColor: "rgba(14, 116, 144, 0.9)",
      tension: 0.35,
      fill: true,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#64748b", maxRotation: 0, minRotation: 0 },
    },
    y: {
      beginAtZero: !isCurrency.value,
      grid: { color: "rgba(148, 163, 184, 0.25)", drawBorder: false },
      ticks: {
        color: "#64748b",
        callback: (value) => formatValue(value),
      },
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context) =>
          `${context.dataset.label}: ${formatValue(context.parsed.y ?? 0)}`,
        afterBody: (tooltipItems) => {
          if (!props.tooltipRows) {
            return [];
          }
          return props.tooltipRows(tooltipItems) || [];
        },
      },
    },
  },
}));
</script>

<template>
  <div class="w-full">
    <Line :data="chartData" :options="chartOptions" class="h-56" />
  </div>
</template>
